import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useActiveCellar } from '../auth/CellarListContext';
import { useCellarStore } from '../store/CellarStoreContext';
import { StockCard } from '../components/StockCard';
import { sortByDomaine } from '../domain/wine-order';
import { EmptyState } from '../components/EmptyState';
import { FilterBar } from '../components/FilterBar';
import { filtersFromParams, filtersToParams } from '../components/filter-params';
import {
  NO_FILTERS,
  filterOptions,
  filterWines,
  hasActiveFilters,
  regionChoices,
} from '../domain/filters';
import { t, tf } from '../i18n';
import './InventoryPage.css';

/**
 * /c/:cellarId/stock, "Stock" (Story 1; owner request 2026-10-01: the home page no longer shows
 * the whole stock): the wines as a grid of cards, A to Z by Domaine, with search and filters and
 * the number of matching wines (User Story 4, T084); wines with no bottles left are hidden unless
 * asked for. The filters live in the address, so they survive opening a wine and coming back, and
 * the home page's cards open this page already filtered (`?alert=past`, `?alert=soon`).
 * Owner, 2026-10-09 and 2026-10-13: the search and filters are folded behind "Rechercher" until
 * asked for, even when a filter is set; the cards are the compact ones (StockCard); the drink-by
 * count beside the title is gone (the home figure "À boire" does the same).
 */
export function InventoryPage() {
  const { cellarId } = useParams<{ cellarId: string }>();
  const { cellar } = useCellarStore();
  const active = useActiveCellar();
  const [params, setParams] = useSearchParams();
  // The page's own state answers every tap and keystroke at once; the address follows, so the
  // filters are still there after opening a wine and coming back. (Reading them only from the
  // address would lag, because the router applies address changes later.)
  const [filters, setFiltersState] = useState(() => filtersFromParams(params));
  // The search and filters stay folded away until asked for, even when a home figure opens Stock
  // already filtered (owner, 2026-10-09 and 2026-10-13).
  const [searching, setSearching] = useState(false);
  const wines = useMemo(() => cellar?.wines ?? [], [cellar]);
  const shown = useMemo(() => sortByDomaine(filterWines(wines, filters)), [wines, filters]);
  const options = useMemo(() => filterOptions(wines), [wines]);

  const setFilters = (next: typeof filters) => {
    setFiltersState(next);
    // Replace, not push: typing in the search must not fill the back button's history.
    setParams(filtersToParams(next), { replace: true });
  };

  if (!cellar) {
    return (
      <main>
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  return (
    <main>
      <header className="inventory-header">
        <div className="inventory-title">
          <h1>{t('nav.stock')}</h1>
        </div>
        <div className="inventory-actions">
          {cellar.wines.length > 0 && (
            <button
              type="button"
              aria-expanded={searching}
              aria-controls="inventory-search-pane"
              onClick={() => setSearching(!searching)}
            >
              {t('inventory.showSearch')}
            </button>
          )}
          <Link to={`/c/${cellarId}/achat/nouveau`} className="cta-action">
            {t('inventory.addPurchase')}
          </Link>
        </div>
      </header>

      {cellar.wines.length === 0 ? (
        <p>{active.own ? t('inventory.empty') : t('inventory.emptyOther')}</p>
      ) : (
        <>
          {searching && (
            <div id="inventory-search-pane">
              <FilterBar
                filters={filters}
                options={options}
                regionsFor={(pays) => regionChoices(wines, pays)}
                onChange={setFilters}
              />
            </div>
          )}
          <p className="inventory-count" aria-live="polite">
            {tf(shown.length === 1 ? 'filters.count.one' : 'filters.count.other', {
              count: String(shown.length),
            })}
            {hasActiveFilters(filters) && (
              <button
                type="button"
                className="inventory-count__reset"
                onClick={() => setFilters(NO_FILTERS)}
              >
                {t('filters.reset')}
              </button>
            )}
          </p>
          {shown.length === 0 ? (
            <EmptyState
              filtered={hasActiveFilters(filters)}
              onReset={() => setFilters(NO_FILTERS)}
              onShowFinished={() => setFilters({ ...filters, includeEmpty: true })}
            />
          ) : (
            <div className="card-grid">
              {shown.map((wine) => (
                <StockCard key={wine.id} cellarId={cellarId!} wine={wine} />
              ))}
            </div>
          )}
        </>
      )}
    </main>
  );
}
